import React from 'react';
import { Box, Typography } from '@mui/material';
import { BulletList, Code, CodeLinks, Field, Prose, Section, SourceRefs } from './DetailPrimitives';
import PolicyParametersSection from './PolicyParametersSection';
import OntologyMeshSection from './OntologyMeshSection';
import { conceptHref, formatDate } from './ontologyFormat';
import { dsVar } from '../../theme/tokens';

/**
 * A policy: what it says, what it obliges, and which concepts it governs.
 *
 * `governs_concepts` holds BOTH the controls the policy sets and the risk signals it is
 * about, so it is rendered as one list of links rather than split — narrowing it to
 * controls would break the citation on any case card that reads off a signal.
 */
const PolicyDetailBody: React.FC<{ document: Record<string, any> }> = ({ document }) => {
    const clauses = Array.isArray(document.clauses) ? document.clauses : [];

    return (
        <Box>
            {document.narrative && <Prose>{document.narrative}</Prose>}

            <Section title="Governance">
                <Field label="Jurisdiction" value={document.jurisdiction} />
                <Field label="Regulator" value={document.regulator} />
                <Field label="Regime" value={document.regime} />
                {/* Who should be reviewing thresholds proposed under this policy. */}
                <Field label="Owner" value={document.owner_role} />
                <Field label="Review cadence" value={document.review_cadence} />
                <Field
                    label="Last reviewed"
                    value={document.last_reviewed_at ? formatDate(document.last_reviewed_at) : null}
                />
            </Section>

            <Section title="Obligations">
                <BulletList items={document.obligations} />
            </Section>

            {/* Supersedes a bare chip list of governs_concepts: the same set, but each
                entry carrying its label and whether a threshold is actually configured
                for it. The codes-only view is still available on the parameters tab. */}
            <PolicyParametersSection
                policyCode={document.policy_code}
                governsConcepts={document.governs_concepts}
            />

            {clauses.length > 0 && (
                <Section title="Clauses">
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {clauses.map((clause: any, index: number) => (
                            <Box
                                key={clause.clause_id || index}
                                sx={{
                                    p: 1.75, borderRadius: '6px',
                                    border: `1px solid ${dsVar('line')}`, backgroundColor: dsVar('page'),
                                }}
                            >
                                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: dsVar('fg-body'), mb: 0.5 }}>
                                    {clause.clause_id && <Code>{clause.clause_id}</Code>}
                                    {clause.heading && (
                                        <Typography component="span" sx={{ ml: clause.clause_id ? 1 : 0, fontSize: '0.875rem' }}>
                                            {clause.heading}
                                        </Typography>
                                    )}
                                </Typography>

                                {clause.text && (
                                    <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-soft'), lineHeight: 1.65, mb: 1 }}>
                                        {clause.text}
                                    </Typography>
                                )}

                                <Field label="Obligation" value={clause.obligation} />

                                {Array.isArray(clause.governs_concepts) && clause.governs_concepts.length > 0 && (
                                    <Box sx={{ mt: 1 }}>
                                        <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted'), mb: 0.5 }}>
                                            Governs
                                        </Typography>
                                        <CodeLinks codes={clause.governs_concepts} href={conceptHref} />
                                    </Box>
                                )}

                                {Array.isArray(clause.sourceRefs ?? clause.source_refs)
                                    && (clause.sourceRefs ?? clause.source_refs).length > 0 && (
                                    <Box sx={{ mt: 1.25 }}>
                                        <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted'), mb: 0.5 }}>
                                            Cites
                                        </Typography>
                                        {/* Keys inside a clause's citations stay camelCase on the
                                            wire — the control plane's case mapper descends one
                                            level only — so read both spellings. */}
                                        <SourceRefs
                                            refs={(clause.sourceRefs ?? clause.source_refs).map((ref: any) => ({
                                                title: ref.title,
                                                document_identifier: ref.documentIdentifier ?? ref.document_identifier,
                                                issuer: ref.issuer,
                                                issuance_date: ref.issuanceDate ?? ref.issuance_date,
                                                source_link: ref.sourceLink ?? ref.source_link,
                                                pinpoint: ref.pinpoint,
                                            }))}
                                        />
                                    </Box>
                                )}
                            </Box>
                        ))}
                    </Box>
                </Section>
            )}

            <OntologyMeshSection kind="policies" code={document.policy_code} />

            <Section title="Sources">
                <SourceRefs refs={document.source_refs} />
            </Section>
        </Box>
    );
};

export default PolicyDetailBody;
